import { enabled } from "@giftforward/entitlements";
import { ArrowBack, LockOutlined, ScienceOutlined } from "@mui/icons-material";
import { Box, Button, Typography } from "@mui/material";
import { useCallback, useEffect, useState } from "react";
import FinanceInvoicesPanel from "@/components/finance/FinanceInvoicesPanel";
import PlanPanel from "@/components/plan/PlanPanel";
import { GfgSelect, GfgTextField } from "@/components/ui";
import AllowancesPanel from "@/components/usersOrgs/AllowancesPanel";
import GroupCreditsPoolPanel from "@/components/usersOrgs/GroupCreditsPoolPanel";
import GroupPaymentMethodsPanel from "@/components/usersOrgs/GroupPaymentMethodsPanel";
import { OrgTransactionsPanel } from "@/components/usersOrgs/GroupTransactionsPanel";
import GroupTree from "@/components/usersOrgs/GroupTree";
import OrgSubscriptionPanel from "@/components/usersOrgs/OrgSubscriptionPanel";
import OrgUsersPanel from "@/components/usersOrgs/OrgUsersPanel";
import TabButton from "@/components/usersOrgs/TabButton";
import { useOrgCreditBalance } from "@/hooks/useCredits";
import { useGroups } from "@/hooks/useGroups";
import { useFinanceInvoices } from "@/hooks/useInvoices";
import { useUpdateOrganization, useUpdateOrganizationSettings } from "@/hooks/useOrganizations";
import { useOrgEntitlements } from "@/hooks/useOrgEntitlements";
import { usePermissions } from "@/hooks/usePermissions";
import { useAccountLeads } from "@/hooks/useUsers";
import type { AccountDeepLink } from "@/interfaces/accountNav";
import type { Organization, OrgSettingsPatch } from "@/interfaces/organization";

const titleCase = (s: string | null) => (s ? s.charAt(0).toUpperCase() + s.slice(1) : "—");

// Lifecycle stage codes → label + badge colors (mirrors the design's .history-status.is-*).
type StageMeta = { label: string; bg: string; color: string };
const ACTIVE_META: StageMeta = {
  label: "Active",
  bg: "var(--color-good-sage-4)",
  color: "var(--color-good-sage)",
};
const LIFECYCLE_META: Record<number, StageMeta> = {
  0: { label: "New", bg: "var(--color-good-night-4)", color: "var(--color-good-night)" },
  1: ACTIVE_META,
  2: { label: "At Risk", bg: "var(--color-good-ochre-4)", color: "var(--color-good-ochre)" },
  3: { label: "Churned", bg: "var(--color-good-wine-4)", color: "var(--color-good-wine)" },
  4: { label: "Reactivated", bg: "var(--color-good-night-4)", color: "var(--color-good-night)" },
};
// Stages a GFG admin may set by hand; "New" is auto-assigned (< 30 days).
const OVERRIDABLE_STAGES = [1, 2, 3, 4];
const lifecycleMeta = (n: number): StageMeta => LIFECYCLE_META[n] ?? ACTIVE_META;

// --- Design tokens for the Details form (from the marketing mockup) ------------
const cardSx = {
  bgcolor: "var(--color-white)",
  border: "1px solid var(--color-smoke)",
  borderRadius: "12px",
  p: 3,
  mb: 2,
} as const;

/** Read-only "locked" field (Plan, Credit balance) — wax value box with a lock. */
function LockedField({ label, value, hint }: { label: string; value: string; hint: string }) {
  return (
    <Box sx={{ flex: 1, minWidth: 0 }}>
      <Typography sx={{ fontSize: 12, color: "var(--color-graphite)", mb: 0.75 }}>
        {label}
      </Typography>
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          gap: 1,
          px: "14px",
          py: "12px",
          border: "1px solid var(--color-smoke)",
          borderRadius: "8px",
          bgcolor: "var(--color-wax)",
          fontFamily: "var(--font-lato)",
          fontSize: 15,
          fontWeight: 600,
          color: "var(--color-rodeo-black)",
        }}
      >
        <span>{value}</span>
        <LockOutlined titleAccess={hint} sx={{ fontSize: 16, color: "var(--color-graphite)" }} />
      </Box>
    </Box>
  );
}

function LifecycleBadge({ stage }: { stage: number }) {
  const m = lifecycleMeta(stage);
  return (
    <span
      style={{
        display: "inline-flex",
        alignItems: "center",
        padding: "2px 10px",
        borderRadius: 20,
        fontSize: 11,
        fontWeight: 700,
        textTransform: "uppercase",
        letterSpacing: "1px",
        background: m.bg,
        color: m.color,
        whiteSpace: "nowrap",
      }}
    >
      {m.label}
    </span>
  );
}

// Wallets / Transactions / Payment Methods live on the GROUP (see GroupView) — a wallet
// belongs to a group, not the org — so they're deliberately absent here.
type OrgTab =
  | "details"
  | "credit"
  | "users"
  | "groups"
  | "wallets"
  | "transactions"
  | "payment-methods"
  | "invoices"
  | "plan"
  | "subscription";

/**
 * GROUPS ARE AN ENTERPRISE CONCEPT. Starter and Pro sell one wallet, so their clients should never
 * meet the word — and the four tabs after Groups are the ROOT GROUP's own surfaces, raised to the
 * organization so nothing is lost by hiding the tree.
 *
 * Ordered so the two arrangements read the same: Groups sits where the money tabs sit, and a client
 * upgrading to Enterprise sees the tree appear in the place those tabs were.
 */
const TABS: { key: OrgTab; label: string }[] = [
  { key: "details", label: "Details" },
  { key: "credit", label: "Allowances" },
  { key: "users", label: "Users" },
  { key: "groups", label: "Groups" },
  { key: "wallets", label: "Wallet" },
  { key: "transactions", label: "Transactions" },
  { key: "payment-methods", label: "Payment Methods" },
  { key: "invoices", label: "Invoices" },
  { key: "plan", label: "Plan" },
  { key: "subscription", label: "Subscription" },
];

/** The root group's surfaces, shown at organization level when the tree is hidden. */
const ROOT_GROUP_TABS = new Set<OrgTab>(["wallets", "payment-methods", "invoices"]);

/**
 * Tabs that have nothing to say about a demo organization (`is_fake`): it holds no credits, is
 * never billed and has no exposure policy, so Credit Settings and the money surfaces would only
 * render empty. Details, Users, Groups (the enterprise demo will carry several) and Subscription
 * (the tier the demo represents) stay.
 */
const DEMO_HIDDEN_TABS = new Set<OrgTab>([
  "credit",
  "wallets",
  "transactions",
  "payment-methods",
  "invoices",
]);

/**
 * Banner atop a demo organization's Details. Its name is locked because it is the key: the Demo
 * tab matches the committed bundle, and the Impact page its generated report, by organization
 * name. Account lead and lifecycle are hidden for it — a demo has neither.
 */
function DemoBanner() {
  return (
    <Box
      sx={{
        display: "flex",
        alignItems: "flex-start",
        gap: 1.25,
        bgcolor: "var(--color-wax)",
        border: "1px solid var(--color-smoke)",
        borderRadius: "10px",
        p: "12px 14px",
        mb: 2.5,
      }}
    >
      <ScienceOutlined sx={{ fontSize: 20, color: "var(--color-dark-dust)", flexShrink: 0 }} />
      <Typography sx={{ fontSize: 13.5, color: "var(--color-rodeo-black)", lineHeight: 1.5 }}>
        <strong>Demo organization.</strong> Seeded from an anonymized legacy bundle for sales
        walkthroughs — static data, no credits, excluded from GfG-wide totals. Reseed or impersonate
        it from Users &amp; Orgs → Demo. The name is fixed by its bundle.
      </Typography>
    </Box>
  );
}

function DetailsPanel({ org }: { org: Organization }) {
  const { data: creditBalance } = useOrgCreditBalance(org.id);
  const { can } = usePermissions();
  // Account lead is a GFG assignment surface (edit USER @ all). Hidden outright from everyone
  // else — it used to render read-only for them — and the list isn't even fetched for them.
  // Neither it nor lifecycle applies to a demo organization (see DemoBanner).
  const canEditLead = can("edit", "USER", "all") && !org.isFake;
  const { data: leads = [], isLoading: leadsLoading } = useAccountLeads(canEditLead);
  // Lifecycle is a GFG-managed field (ORGANIZATION edit @ "all"). Client Admins / others
  // don't see it at all, and the BE rejects the change too.
  const canEditLifecycle = can("edit", "ORGANIZATION", "all") && !org.isFake;
  // name → the org-scope rename route; account lead / lifecycle → the GFG-only /settings route.
  const updateName = useUpdateOrganization();
  const updateSettings = useUpdateOrganizationSettings();
  const saving = updateName.isPending || updateSettings.isPending;
  const [saveError, setSaveError] = useState<string | null>(null);
  const [savedOk, setSavedOk] = useState(false);

  // Editable fields. `org` is a blotter snapshot that never refreshes after a save,
  // so we keep both the working value and a locally-tracked saved baseline.
  const [name, setName] = useState(org.name);
  const [leadId, setLeadId] = useState<number | "">(org.accountLeadId ?? "");
  const [stage, setStage] = useState<number>(org.lifecycle);
  const [saved, setSaved] = useState({
    name: org.name,
    leadId: org.accountLeadId ?? ("" as number | ""),
    stage: org.lifecycle,
  });

  const trimmedName = name.trim();
  const nameValid = trimmedName.length > 0;
  const dirty = trimmedName !== saved.name || leadId !== saved.leadId || stage !== saved.stage;

  async function handleSave() {
    setSaveError(null);
    setSavedOk(false);
    try {
      const tasks: Promise<void>[] = [];
      // name is org-scope editable (own-org rename) → its own route.
      if (trimmedName !== saved.name) {
        tasks.push(updateName.mutateAsync({ id: org.id, patch: { name: trimmedName } }));
      }
      // account lead / lifecycle are GFG-only → the /settings route (gated there, not here).
      const settingsPatch: OrgSettingsPatch = {};
      if (canEditLead && leadId !== saved.leadId) {
        settingsPatch.accountLeadId = leadId === "" ? null : leadId;
      }
      // "New" (0) is auto-only and never sent; the select can't select it.
      if (canEditLifecycle && stage !== saved.stage) {
        settingsPatch.lifecycle = stage;
      }
      if (Object.keys(settingsPatch).length > 0) {
        tasks.push(updateSettings.mutateAsync({ id: org.id, patch: settingsPatch }));
      }

      await Promise.all(tasks);
      setSaved({ name: trimmedName, leadId, stage });
      setSavedOk(true);
    } catch (e) {
      setSaveError(e instanceof Error ? e.message : "Failed to save. Please retry.");
    }
  }

  return (
    <Box sx={{ maxWidth: 780 }}>
      {org.isFake && <DemoBanner />}
      {/* Organization details */}
      <Box sx={cardSx}>
        <Box sx={{ mb: 2.5 }}>
          {org.isFake ? (
            <LockedField
              label="Organization name"
              value={org.name}
              hint="Fixed by the demo bundle"
            />
          ) : (
            <GfgTextField
              label="Organization name"
              value={name}
              onChange={(e) => setName(e.target.value)}
            />
          )}
        </Box>

        <Box sx={{ display: "flex", gap: 1.75, flexWrap: "wrap", mb: 2.5 }}>
          <LockedField label="Plan" value={titleCase(org.plan)} hint="Managed via Plan / Billing" />
          <LockedField
            label="Credit balance"
            value={
              creditBalance != null ? `${Number(creditBalance).toLocaleString()} credits` : "—"
            }
            hint="Adjusted via credit purchases and campaigns"
          />
        </Box>

        {/* GFG-only, like Lifecycle stage below. A client has no use for who inside GFG owns
            their account, so it is not shown to them at all rather than shown read-only. */}
        {canEditLead && (
          <GfgSelect
            label="Account lead"
            value={leadId === "" ? "" : String(leadId)}
            onChange={(e) => setLeadId(e.target.value === "" ? "" : Number(e.target.value))}
            disabled={leadsLoading}
            options={[
              { value: "", label: "Unassigned" },
              ...leads.map((l) => ({ value: String(l.id), label: l.name })),
            ]}
          />
        )}
      </Box>

      {/* Lifecycle stage — GFG-only (ORGANIZATION edit @ "all"); not loaded for others. */}
      {canEditLifecycle && (
        <Box sx={cardSx}>
          <Typography sx={{ fontSize: 15, fontWeight: 700, color: "var(--color-rodeo-black)" }}>
            Lifecycle stage
          </Typography>
          <Typography sx={{ fontSize: 13, color: "var(--color-graphite)", mt: 0.5, mb: 2 }}>
            Where this account sits in its customer journey. Normally set automatically by the
            churn-evaluation job; override it here after you&rsquo;ve worked a save with the client.
          </Typography>

          <Box sx={{ display: "flex", alignItems: "center", gap: 1.75 }}>
            <Box sx={{ flex: 1 }}>
              <GfgSelect
                label="Stage"
                value={String(stage)}
                onChange={(e) => setStage(Number(e.target.value))}
                options={[
                  ...(stage === 0
                    ? [{ value: "0", label: "New — set automatically (<30 days)", disabled: true }]
                    : []),
                  ...OVERRIDABLE_STAGES.map((s) => ({
                    value: String(s),
                    label: lifecycleMeta(s).label,
                  })),
                ]}
              />
            </Box>
            <LifecycleBadge stage={stage} />
          </Box>

          <Typography
            sx={{
              fontSize: 12.5,
              color: "var(--color-graphite)",
              mt: 1.75,
              pt: 1.75,
              borderTop: "1px solid var(--color-smoke)",
            }}
          >
            Activity signals (last activity, campaigns, lifetime value) coming soon.
          </Typography>
        </Box>
      )}

      {/* Save — nothing on a demo organization's Details is editable, so no button to press. */}
      {!org.isFake && (
        <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
          <Button
            onClick={handleSave}
            variant="contained"
            disabled={!dirty || !nameValid || saving}
            sx={{ bgcolor: "var(--color-rodeo-black)" }}
          >
            {saving ? "Saving…" : "Save changes"}
          </Button>
          {saveError && (
            <Typography sx={{ fontSize: 13, color: "error.main" }}>{saveError}</Typography>
          )}
          {savedOk && !dirty && (
            <Typography sx={{ fontSize: 13, color: "var(--color-graphite)" }}>Saved.</Typography>
          )}
        </Box>
      )}
    </Box>
  );
}

export default function OrgDetail({
  org,
  onBack,
  restrictToUsersTab = false,
  deepLink,
}: {
  org: Organization;
  onBack?: () => void;
  /** GFG Admin's own-org Account view: only the Users tab applies (see Account.tsx). */
  restrictToUsersTab?: boolean;
  /**
   * One-shot deep-link message from the router state (Account is its only reader). `tab` names
   * the tab to open; `groupId`/`groupTab` are forwarded to GroupTree once and only once. The
   * names are validated below against the tabs this caller can actually see, and anything
   * unknown falls back to whatever tab is already open.
   */
  deepLink?: AccountDeepLink | null;
}) {
  const { can, isGfg } = usePermissions();
  const [tab, setTab] = useState<OrgTab>(restrictToUsersTab ? "users" : "details");

  /**
   * Whether this organization sees GROUPS at all.
   *
   * `wallet.multi_group` is the same entitlement the server enforces when a second group is
   * created and the campaign builder already uses to decide whether to offer a wallet picker —
   * so an Enterprise upgrade reveals the tree on its own, with no second switch to keep in step.
   *
   * The `> 1` clause is not belt and braces. An organization that downgrades from Enterprise KEEPS
   * the groups it made, and hiding the tree would strand the credits sitting in them: invisible,
   * unreachable, and still real money. The concept is hidden from clients who never had it, never
   * from a client who has something in it.
   */
  const { ent } = useOrgEntitlements(org.id);
  const { data: groups } = useGroups(org.id);
  /**
   * Does this organization have any invoices at all? An empty Invoices tab is a dead end — it
   * costs a click to learn there is nothing there, and for the many clients who never receive one
   * it is permanently empty. So the tab appears only once there is something behind it.
   *
   * A `limit: 1` probe, read for its `total` rather than its rows: the panel does its own full
   * paginated fetch when the tab is actually opened, so this is a count and not a duplicate load.
   * Gated on the same capability as the tab, so a caller without it never fires a request the
   * route would reject.
   *
   * While it is in flight `total` is undefined and the tab is hidden, matching how the
   * capability-gated tabs behave before caps arrive: it appears when the answer does.
   */
  const canViewInvoices = can("view", "CREDIT");
  const { data: invoiceProbe } = useFinanceInvoices(
    { orgId: org.id, limit: 1 },
    { enabled: canViewInvoices },
  );
  const hasInvoices = (invoiceProbe?.total ?? 0) > 0;
  const rootGroup = groups?.find((g) => g.parentId === null) ?? null;
  const rootGroupId = rootGroup?.id ?? null;
  const showGroups =
    // GFG staff always see the tree. The requirement is that a CLIENT never meets groups, not that
    // we lose the surface: invoice creation, per-group payment methods and the rest live on the
    // group panels, so hiding the tree from GFG removed the only route to them. An impersonated
    // session carries isGfg=false and correctly sees the client's arrangement.
    isGfg || (!!ent && enabled(ent, "wallet", "multi_group")) || (groups?.length ?? 0) > 1;
  // Credit Settings carries the org's credit POLICY, so it is gated on edit CREDIT @ all — the same
  // capability the backend enforces on /credit-settings, and narrower than org admin: a GFG Admin
  // can rename the org and set its lifecycle without being able to touch its exposure limit.
  // Plan is Client-Admin-only (PRD F6 / Q6): TIER *edit* is the capability only clientAdmin holds
  // (orgViewOnly has TIER view and must not see it), and GFG staff browsing Users & Orgs never do.
  // An impersonated session carries the impersonated admin's caps with isGfg=false, so the tab is
  // there and PlanPanel disables the actions instead (PRD F7).
  const visibleTabs = restrictToUsersTab
    ? TABS.filter((t) => t.key === "users")
    : TABS.filter((t) => {
        if (org.isFake && DEMO_HIDDEN_TABS.has(t.key)) return false;
        if (t.key === "credit") return can("edit", "CREDIT", "all");
        if (t.key === "plan") return can("edit", "TIER") && !isGfg;
        // The billing record BEHIND the plan, for GFG staff. Gated on the capability rather than
        // on a role name: TIER at scope `all` is held only by GFG roles — client admins hold it at
        // org scope — so this is GFG-only by construction, and roles are deliberately not ranked
        // in this codebase.
        if (t.key === "subscription") return can("view", "TIER", "all");
        // The tree, or the root group's surfaces raised to org level — never both, or the same
        // wallet would appear twice under two names.
        if (t.key === "groups") return showGroups;
        // Org-scoped, so it needs no root group, and shown to GFG as well: one organization's
        // invoices on that organization's page is useful whether or not the tree is there. `view`
        // CREDIT is what its endpoint actually requires — gating it on anything else is how the
        // first version of this tab shipped a 403. `hasInvoices` then drops the tab for an
        // organization that has never been invoiced, rather than offering an empty table.
        if (t.key === "invoices") return canViewInvoices && hasInvoices;
        // Org-scoped like Invoices, so shown with or without the tree: every group's purchases in
        // one list, which is where a client looks for a receipt. Same capability as its endpoint.
        if (t.key === "transactions") return can("view", "CREDIT");
        if (ROOT_GROUP_TABS.has(t.key)) {
          // Same capability GroupView gates these on, so raising them changes where they live and
          // not who may see them. Needs a root group to point at.
          return !showGroups && rootGroupId !== null && can("view", "CREDIT");
        }
        return true;
      });

  // Resolving through the filtered list both validates the deep link and narrows it to an OrgTab.
  // Null until caps load, so a link to a capability-gated tab still lands once they arrive.
  const requestedTab = visibleTabs.find((t) => t.key === deepLink?.tab)?.key ?? null;
  const { nonce, groupId: linkGroupId, groupTab } = deepLink ?? {};

  // The group half of the message, handed to GroupTree exactly once per navigation: GroupTree
  // unmounts whenever another tab is showing, so if it read the router state itself every mount
  // would replay the last deep link (re-opening that group, on that tab, forever).
  const [groupDeepLink, setGroupDeepLink] = useState<AccountDeepLink | null>(null);
  const clearGroupDeepLink = useCallback(() => setGroupDeepLink(null), []);

  // Sync, not just initialize: navigating /account → /account re-renders this component without
  // remounting it, so a second deep link still has to move the tab. Keyed on the message's
  // `nonce`, so requesting the SAME tab again is still a new event — while a manual tab click,
  // which produces no new nonce, is left alone.
  useEffect(() => {
    if (requestedTab) setTab(requestedTab);
    if (linkGroupId != null || groupTab) {
      setGroupDeepLink({ nonce, groupId: linkGroupId, groupTab });
    }
  }, [nonce, requestedTab, linkGroupId, groupTab]);

  return (
    <Box>
      {/* Back to the Organizations blotter. Omitted when there's no blotter to return to
          (e.g. the Account view renders this for the caller's own org). */}
      {onBack && (
        <button
          type="button"
          onClick={onBack}
          style={{
            display: "inline-flex",
            alignItems: "center",
            gap: 4,
            border: "none",
            background: "none",
            cursor: "pointer",
            color: "var(--color-graphite)",
            fontFamily: "var(--font-lato)",
            fontSize: 14,
            padding: 0,
            marginBottom: 12,
          }}
        >
          <ArrowBack sx={{ fontSize: 18 }} /> Organizations
        </button>
      )}

      <h2
        style={{
          fontFamily: "var(--font-crimson)",
          fontSize: 28,
          fontWeight: 400,
          color: "var(--color-rodeo-black)",
          margin: "0 0 18px",
        }}
      >
        {org.name}
      </h2>

      <div
        role="tablist"
        style={{
          display: "flex",
          gap: 24,
          borderBottom: "1px solid var(--color-card-border)",
          marginBottom: 24,
        }}
      >
        {visibleTabs.map((t) => (
          <TabButton
            key={t.key}
            active={tab === t.key}
            label={t.label}
            onClick={() => setTab(t.key)}
            id={t.key === "users" ? "tour-account-tab-users" : undefined}
          />
        ))}
      </div>

      {tab === "details" && <DetailsPanel org={org} />}
      {tab === "credit" && <AllowancesPanel org={org} />}
      {tab === "users" && (
        <div id="tour-account-panel-users">
          <OrgUsersPanel org={org} />
        </div>
      )}
      {tab === "groups" && (
        <GroupTree org={org} deepLink={groupDeepLink} onDeepLinkConsumed={clearGroupDeepLink} />
      )}
      {/* The ROOT GROUP's own panels, mounted here rather than reimplemented. These endpoints are
          group-scoped and the root group is a real group, so nothing about the data changes —
          only where a client finds it. Enterprise reaches the identical panels through the tree. */}
      {rootGroup && tab === "wallets" && (
        <GroupCreditsPoolPanel
          groupId={rootGroup.id}
          // The organization's name, not the root group's. They are the same string today, but the
          // client is being shown their ORGANISATION's wallet — naming the group would reintroduce
          // the word this whole arrangement exists to hide.
          groupName={org.name}
          organizationId={org.id}
          canPurchase
          // Same courtesy as the group view: the remedy for "no card" lives on another tab, so the
          // button goes there rather than sitting disabled.
          onAddPaymentMethod={() => setTab("payment-methods")}
        />
      )}
      {tab === "transactions" && (
        <OrgTransactionsPanel
          orgId={org.id}
          // Transfers are per group: shown here only while the org stands in for its root group.
          transfersGroupId={showGroups ? null : rootGroupId}
          showGroup={showGroups}
        />
      )}
      {rootGroup && tab === "payment-methods" && (
        // A root group may always purchase — GroupView derives exactly this from `parentId === null`.
        <GroupPaymentMethodsPanel groupId={rootGroup.id} canPurchase />
      )}
      {/* The FINANCE invoice list pinned to this organization — NOT GroupInvoicesPanel, which reads
          /admin/groups/:id/invoices. That route is gated on `send_invoice` CREDIT, reads included,
          because it is the invoice MANAGEMENT surface: only GFG admin roles hold it, so mounting it
          for a client produced a tab that 403'd on open. This one is `view` CREDIT with a
          scopeGuard, which is the client's own invoices by construction. */}
      {tab === "invoices" && <FinanceInvoicesPanel pinnedOrgId={org.id} />}
      {tab === "plan" && <PlanPanel />}
      {tab === "subscription" && <OrgSubscriptionPanel org={org} />}
    </Box>
  );
}
